import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import App from './App.jsx';
import LoginGate from './auth/LoginGate.jsx';
import { initializeMsal, msalInstance } from './auth/msal.js';
import { initTheme } from './utils/theme.js';
import { installConsoleCapture } from './utils/consoleCapture.js';
import ToastContainer from './components/ui/ToastContainer.jsx';
import ConfirmModal from './components/ui/ConfirmModal.jsx';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/index.css';
import './styles/help.css';
import './styles/dylan-embed.css';

// Remote Access Tools pop-out windows (opened via window.open from
// RemoteToolsModal). There is no react-router mounted in this app —
// tabs are switched via Zustand state, not URL routes — so rather than
// introducing a router for two windows, main.jsx just inspects
// window.location.pathname before the first render and swaps in a
// standalone page in place of <App/>. staticwebapp.config.json already
// rewrites unknown paths to /index.html, so these paths work in both
// dev (Vite's default SPA fallback) and the deployed static build.
// Both pages share the same MsalProvider + LoginGate as the main app,
// so they reuse the cached MSAL session (localStorage cacheLocation)
// instead of forcing a second interactive login in the pop-out window.
const RemoteTerminalPage = lazy(() => import('./pages/RemoteTerminalPage.jsx'));
const RemoteFilesPage = lazy(() => import('./pages/RemoteFilesPage.jsx'));
const RemoteRegistryPage = lazy(() => import('./pages/RemoteRegistryPage.jsx'));
const RemoteChatPage = lazy(() => import('./pages/RemoteChatPage.jsx'));

function resolveStandalonePage() {
  switch (window.location.pathname) {
    case '/remote-tools/terminal': return RemoteTerminalPage;
    case '/remote-tools/files': return RemoteFilesPage;
    case '/remote-tools/registry': return RemoteRegistryPage;
    case '/remote-tools/chat': return RemoteChatPage;
    default: return null;
  }
}

// Stale-chunk recovery: after a new deploy the hashed lazy chunks change,
// but a tab still running the previous build holds an index.html that
// references the old chunk names. When it tries to lazy-load one, Vite
// fires `vite:preloadError`. Reload once (guarded against a loop) to pull
// the fresh index.html + chunks — this is what a manual Ctrl+F5 did.
const RELOAD_FLAG = 'vite-preload-reloaded';
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  if (!sessionStorage.getItem(RELOAD_FLAG)) {
    sessionStorage.setItem(RELOAD_FLAG, '1');
    window.location.reload();
  }
});
window.addEventListener('load', () => {
  window.setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 2000);
});

// Apply the stored light/dark choice before first paint (no flash).
initTheme();

const root = ReactDOM.createRoot(document.getElementById('root'));

async function bootstrap() {
  await initializeMsal();
  const StandalonePage = resolveStandalonePage();

  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <LoginGate>
          {StandalonePage ? (
            <>
              <Suspense fallback={<div style={{ padding: 40, fontFamily: 'sans-serif', color: 'var(--muted)' }}>Loading…</div>}>
                <StandalonePage />
              </Suspense>
              {/* Standalone pages render outside <App/>, so they need their
                  own toast/confirm hosts — useToast()/useConfirm() just push
                  to the shared Zustand store; nothing renders them otherwise. */}
              <ConfirmModal />
              <ToastContainer />
            </>
          ) : (
            <App />
          )}
        </LoginGate>
      </MsalProvider>
    </React.StrictMode>
  );
}

bootstrap().catch((error) => {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize auth runtime.', error);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
});
